import { createApp, reactive, type Component } from 'vue';
import { createVuetify } from 'vuetify';
import * as components from 'vuetify/components';
import * as directives from 'vuetify/directives';
// ru-локаль Vuetify: переводы компонентов + делает v-date-picker русским
// (месяцы/дни недели берутся из date-адаптера, который берёт локаль отсюда —
// per-component `locale="ru"` на v-date-picker НЕ работает, такого пропа нет).
import { ru } from 'vuetify/locale';

// CSS variables и design tokens — обязательно ДО импорта Vuetify-стилей,
// чтобы overrides применились к первому рендеру.
import './styles/md2-overrides.scss';

import 'vuetify/styles';
import '@mdi/font/css/materialdesignicons.css';

import App from './App.vue';

interface MenuItem { name: string; link: string; subtitle?: string; action?: string }
interface AboutMenu { name: string; submenu: MenuItem[] }
interface LayoutData {
  isAuthenticated: boolean;
  drawer: boolean | null;
  mainmenu: MenuItem[];
  aboutmenu: AboutMenu;
  adminmenu?: MenuItem[];
}

interface PageDispatchEntry {
  mountId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  loader: () => Promise<{ default: Component<any, any, any> }>;
}

const pageDispatchTable: PageDispatchEntry[] = [
  // Phase 3: SFC-migration. Each entry maps mount-point → dynamic import.
  // Add new entries here as new pages migrate.
  {
    mountId: 'groupOfClassesRoot',
    loader: () => import('./components/GroupOfClasses.vue'),
  },
  {
    mountId: 'shortCoursesListRoot',
    loader: () => import('./components/ShortCoursesList.vue'),
  },
  {
    mountId: 'entryFormRoot',
    loader: () => import('./components/EntryForm.vue'),
  },
  {
    mountId: 'offsiteEventEditRoot',
    loader: () => import('./components/OffsiteEventEdit.vue'),
  },
  {
    mountId: 'newsSetsEditRoot',
    loader: () => import('./components/NewsSetsEdit.vue'),
  },
  {
    mountId: 'announcesEditRoot',
    loader: () => import('./components/AnnouncesEdit.vue'),
  },
  {
    mountId: 'teacherSetsEditRoot',
    loader: () => import('./components/TeacherSetsEdit.vue'),
  },
  {
    mountId: 'roomsEditRoot',
    loader: () => import('./components/RoomsEdit.vue'),
  },
  {
    mountId: 'shortCoursesEditRoot',
    loader: () => import('./components/ShortCoursesEdit.vue'),
  },
  {
    mountId: 'coursesEditRoot',
    loader: () => import('./components/CoursesEdit.vue'),
  },
  {
    mountId: 'legkoRoot',
    loader: () => import('./components/Legko.vue'),
  },
  {
    mountId: 'therapistAssistantsRoot',
    loader: () => import('./components/TherapistAssistants.vue'),
  },
  {
    mountId: 'priceEditRoot',
    loader: () => import('./components/PriceEdit.vue'),
  },
  {
    mountId: 'priceListRoot',
    loader: () => import('./components/PriceList.vue'),
  },
  {
    mountId: 'toolsSetEditRoot',
    loader: () => import('./components/ToolsSetEdit.vue'),
  },
  {
    mountId: 'galleryEditRoot',
    loader: () => import('./components/GalleryEdit.vue'),
  },
  {
    mountId: 'galleryIndexRoot',
    loader: () => import('./components/GalleryIndex.vue'),
  },
  {
    mountId: 'newsSetsListRoot',
    loader: () => import('./components/NewsSetsList.vue'),
  },
  {
    mountId: 'announcesListRoot',
    loader: () => import('./components/AnnouncesList.vue'),
  },
  {
    mountId: 'teacherSetsListRoot',
    loader: () => import('./components/TeacherSetsList.vue'),
  },
  {
    mountId: 'toolsSetsListRoot',
    loader: () => import('./components/ToolsSetsList.vue'),
  },
  {
    mountId: 'adminShortCoursesListRoot',
    loader: () => import('./components/AdminShortCoursesList.vue'),
  },
  {
    mountId: 'coursesListRoot',
    loader: () => import('./components/CoursesList.vue'),
  },
  {
    mountId: 'offsiteEventsListRoot',
    loader: () => import('./components/OffsiteEventsList.vue'),
  },
  {
    mountId: 'userEntriesRoot',
    loader: () => import('./components/UserEntries.vue'),
  },
  {
    mountId: 'alarmSettingsRoot',
    loader: () => import('./components/AlarmSettings.vue'),
  },
  {
    mountId: 'adminLegkoRoot',
    loader: () => import('./components/AdminLegko.vue'),
  },
  {
    mountId: 'roomsListRoot',
    loader: () => import('./components/RoomsList.vue'),
  },
  {
    mountId: 'scheduleSetIndexRoot',
    loader: () => import('./components/ScheduleSetIndex.vue'),
  },
  {
    mountId: 'recordRestrictListRoot',
    loader: () => import('./components/RecordRestrictList.vue'),
  },
  {
    mountId: 'recordRestrictEditRoot',
    loader: () => import('./components/RecordRestrictEdit.vue'),
  },
  {
    mountId: 'beginnersListRoot',
    loader: () => import('./components/BeginnersList.vue'),
  },
  {
    mountId: 'beginnersEditRoot',
    loader: () => import('./components/BeginnersEdit.vue'),
  },
  {
    mountId: 'studentsIndexRoot',
    loader: () => import('./components/StudentsIndex.vue'),
  },
  {
    mountId: 'commentsIndexRoot',
    loader: () => import('./components/CommentsIndex.vue'),
  },
  {
    mountId: 'commentsEditRoot',
    loader: () => import('./components/CommentsEdit.vue'),
  },
  {
    mountId: 'filesRepoIndexRoot',
    loader: () => import('./components/FilesRepoIndex.vue'),
  },
  {
    mountId: 'filesRepoEditRoot',
    loader: () => import('./components/FilesRepoEdit.vue'),
  },
  {
    mountId: 'filesRepoDetailsRoot',
    loader: () => import('./components/FilesRepoDetails.vue'),
  },
  {
    mountId: 'articlesIndexRoot',
    loader: () => import('./components/ArticlesIndex.vue'),
  },
  {
    mountId: 'articlesEditRoot',
    loader: () => import('./components/ArticlesEdit.vue'),
  },
  {
    mountId: 'serviceIndexRoot',
    loader: () => import('./components/ServiceIndex.vue'),
  },
  {
    mountId: 'serviceEditRoot',
    loader: () => import('./components/ServiceEdit.vue'),
  },
  {
    mountId: 'stagesIndexRoot',
    loader: () => import('./components/StagesIndex.vue'),
  },
  {
    mountId: 'stagesEditRoot',
    loader: () => import('./components/StagesEdit.vue'),
  },
  {
    mountId: 'seminarSetsIndexRoot',
    loader: () => import('./components/SeminarSetsIndex.vue'),
  },
  {
    mountId: 'seminarSetsEditRoot',
    loader: () => import('./components/SeminarSetsEdit.vue'),
  },
];

declare global {
  interface Window {
    __VUE_LAYOUT_DATA__?: LayoutData;
    __VUE_PAGE_DATA__?: Record<string, unknown>;
  }
}

const vuetify = createVuetify({
  components,
  directives,
  locale: {
    locale: 'ru',
    fallback: 'ru',
    messages: { ru },
  },
  theme: {
    defaultTheme: 'light',
    themes: {
      light: {},
      dark: {},
    },
  },
});

function submitLogoutForm() {
  const f = document.getElementById('logoutForm') as HTMLFormElement | null;
  if (f) f.submit();
}

(window as unknown as { submitLogoutForm: () => void }).submitLogoutForm = submitLogoutForm;

// 1) Layout app: App.vue на #rootId (если layout задан Razor-лейаутом).
const layoutRoot = document.getElementById('rootId');
if (layoutRoot) {
  const layoutData: LayoutData = window.__VUE_LAYOUT_DATA__ ?? {
    isAuthenticated: false,
    drawer: null,
    mainmenu: [],
    aboutmenu: { name: 'ABOUT', submenu: [] },
  };
  const layoutApp = createApp(App as Component, { layoutData });
  layoutApp.use(vuetify);
  layoutApp.mount('#rootId');
}

// 2) Page-specific SFC: монтируем на свой mount-point, если Razor-вью его объявило.
//    Страница кладёт данные в window.__VUE_PAGE_DATA__[mountId] до загрузки main.ts.
const pageData = window.__VUE_PAGE_DATA__ ?? {};
for (const entry of pageDispatchTable) {
  const mountPoint = document.getElementById(entry.mountId);
  if (!mountPoint) continue;
  // Глубокая реактивность page-data критична: admin Edit-SFC привязывают инпуты
  // через v-model к ВЛОЖЕННЫМ свойствам пропа (v-model="field.Header",
  // field.Blocks[0].Block …). Vue оборачивает props в shallowReadonly — сам
  // вложенный объект остаётся плоским/нереактивным, поэтому мутация field.Header
  // не вызывает ре-рендер, Vuetify не получает обновлённый model-value и на blur
  // перерисовывает input на исходное значение (введённый текст «откатывается»).
  // reactive() делает вложенные объекты реактивными; nested-прокси проходят через
  // shallowReadonly get без распаковки, так что v-model работает двусторонне.
  const props = reactive(pageData[entry.mountId] ?? {});
  entry.loader().then((mod) => {
    const pageApp = createApp(mod.default, props);
    pageApp.use(vuetify);
    pageApp.mount(`#${entry.mountId}`);
  });
}
